import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Badge,
    ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ChartCard from "./ChartCard";


export default function DashboardStats() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('mois');

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=dashboard`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des statistiques...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchDashboard}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const totalProduits = data?.stock?.total_produits || 1;
    const produitsRupture = data?.stock?.rupture || 0;
    const produitsCritique = data?.stock?.critique || 0;
    const produitsNormaux = totalProduits - produitsRupture - produitsCritique;

    const tauxRupture = (produitsRupture / totalProduits * 100).toFixed(1);
    const tauxCritique = (produitsCritique / totalProduits * 100).toFixed(1);
    const tauxNormal = (produitsNormaux / totalProduits * 100).toFixed(1);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-dashboard-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Tableau de bord</h4>
                                    <p className="text-muted small mb-0">
                                        Vue d'ensemble de l'activité • {new Date().toLocaleDateString('fr-FR')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                onClick={fetchDashboard}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                            >
                                <i className="ri-refresh-line"></i>
                                Actualiser
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stock/etat/instantane')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Cartes CA - style value cards */}
                    <Row className="g-4 mb-4">
                        <Col lg={4}>
                            <div className="value-card value-card-primary">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-calendar-line me-1"></i>
                                        Aujourd'hui
                                    </div>
                                    <h3 className="value-card-amount">{formatCurrency(data?.ca_jour || 0)}</h3>
                                    <p className="value-card-footer">Chiffre d'affaires</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-money-euro-circle-line"></i>
                                </div>
                            </div>
                        </Col>
                        <Col lg={4}>
                            <div className="value-card value-card-success">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-calendar-2-line me-1"></i>
                                        Ce mois
                                    </div>
                                    <h3 className="value-card-amount">{formatCurrency(data?.ca_mois || 0)}</h3>
                                    <p className="value-card-footer">Chiffre d'affaires mensuel</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-bar-chart-2-line"></i>
                                </div>
                            </div>
                        </Col>
                        <Col lg={4}>
                            <div className="value-card value-card-warning">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-calendar-check-line me-1"></i>
                                        Cette année
                                    </div>
                                    <h3 className="value-card-amount">{formatCurrency(data?.ca_an || 0)}</h3>
                                    <p className="value-card-footer">Chiffre d'affaires annuel</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-line-chart-line"></i>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-group-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Clients</span>
                                    <h3 className="kpi-value">{formatNumber(data?.clients || 0)}</h3>
                                    <span className="kpi-unit">total</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-truck-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Fournisseurs</span>
                                    <h3 className="kpi-value">{formatNumber(data?.fournisseurs || 0)}</h3>
                                    <span className="kpi-unit">actifs</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-shopping-cart-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Commandes en cours</span>
                                    <h3 className="kpi-value">{data?.commandes?.en_cours || 0}</h3>
                                    <span className="kpi-unit">à traiter</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-check-double-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Commandes reçues</span>
                                    <h3 className="kpi-value">{data?.commandes?.recues || 0}</h3>
                                    <span className="kpi-unit">ce mois</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Section Stock et Valeur */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-stack-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">État du stock</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="pt-0">
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge className="rounded-pill px-3 py-2" style={{ background: '#ef4444' }}>Rupture</Badge>
                                                <span className="fw-semibold">{produitsRupture} produits</span>
                                            </div>
                                            <span className="text-muted">{tauxRupture}%</span>
                                        </div>
                                        <div className="progress-bar-custom">
                                            <div className="progress-fill" style={{ width: `${parseFloat(tauxRupture)}%`, background: '#ef4444' }}></div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge className="rounded-pill px-3 py-2" style={{ background: '#f59e0b' }}>Critique</Badge>
                                                <span className="fw-semibold">{produitsCritique} produits</span>
                                            </div>
                                            <span className="text-muted">{tauxCritique}%</span>
                                        </div>
                                        <div className="progress-bar-custom">
                                            <div className="progress-fill" style={{ width: `${parseFloat(tauxCritique)}%`, background: '#f59e0b' }}></div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge className="rounded-pill px-3 py-2" style={{ background: '#10b981' }}>Normal</Badge>
                                                <span className="fw-semibold">{produitsNormaux} produits</span>
                                            </div>
                                            <span className="text-muted">{tauxNormal}%</span>
                                        </div>
                                        <div className="progress-bar-custom">
                                            <div className="progress-fill" style={{ width: `${parseFloat(tauxNormal)}%`, background: '#10b981' }}></div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-pie-chart-2-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">Valeur du stock</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="pt-0">
                                    <div className="text-center mb-4">
                                        <div className="stock-value-circle">
                                            <div>
                                                <h3 className="mb-0 text-primary" style={{ color: '#21ACCD' }}>{formatNumber(data?.stock?.total_produits || 0)}</h3>
                                                <small className="text-muted">produits</small>
                                            </div>
                                        </div>
                                    </div>
                                    <Row className="g-3">
                                        <Col xs={6}>
                                            <div className="bg-light p-3 rounded-3 text-center">
                                                <small className="text-muted d-block">Valeur achat</small>
                                                <h5 className="mb-0 text-primary" style={{ color: '#21ACCD' }}>{formatCurrency(data?.valeur_stock?.valeur_achat || 0)}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="bg-light p-3 rounded-3 text-center">
                                                <small className="text-muted d-block">Valeur vente</small>
                                                <h5 className="mb-0 text-success">{formatCurrency(data?.valeur_stock?.valeur_vente || 0)}</h5>
                                            </div>
                                        </Col>
                                    </Row>
                                    {data?.valeur_stock?.valeur_achat > 0 && (
                                        <div className="mt-3 text-center">
                                            <small className="text-muted">Marge potentielle</small>
                                            <h6 className="text-success">
                                                {formatCurrency((data?.valeur_stock?.valeur_vente || 0) - (data?.valeur_stock?.valeur_achat || 0))}
                                                <small className="text-muted ms-2">
                                                    ({(((data?.valeur_stock?.valeur_vente || 0) - (data?.valeur_stock?.valeur_achat || 0)) / (data?.valeur_stock?.valeur_achat || 1) * 100).toFixed(1)}%)
                                                </small>
                                            </h6>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Alertes stock */}
                    {(produitsRupture > 0 || produitsCritique > 0) && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: '#f8fafc' }}>
                            <Card.Body className="p-4">
                                <h6 className="mb-3 fw-semibold">
                                    <i className="ri-notification-3-line me-2 text-warning"></i>
                                    Alertes stock
                                </h6>
                                <Row className="g-3">
                                    {produitsRupture > 0 && (
                                        <Col md={6}>
                                            <div className="alert-card d-flex align-items-center p-3 bg-white rounded-3">
                                                <div className="alert-icon bg-danger bg-opacity-10 me-3">
                                                    <i className="ri-close-circle-line text-danger fs-4"></i>
                                                </div>
                                                <div>
                                                    <small className="text-danger fw-bold">Rupture de stock</small>
                                                    <h5 className="mb-0">{produitsRupture} produit(s)</h5>
                                                    <Button variant="link" className="p-0 text-danger" size="sm" onClick={() => navigate('/stock/etat/critique')}>
                                                        Voir détails →
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                    {produitsCritique > 0 && (
                                        <Col md={6}>
                                            <div className="alert-card d-flex align-items-center p-3 bg-white rounded-3">
                                                <div className="alert-icon bg-warning bg-opacity-10 me-3">
                                                    <i className="ri-alert-line text-warning fs-4"></i>
                                                </div>
                                                <div>
                                                    <small className="text-warning fw-bold">Stock critique</small>
                                                    <h5 className="mb-0">{produitsCritique} produit(s)</h5>
                                                    <Button variant="link" className="p-0 text-warning" size="sm" onClick={() => navigate('/stock/etat/critique')}>
                                                        Voir détails →
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Liens rapides */}
                    <h5 className="mb-3 fw-semibold">
                        <i className="ri-apps-line me-2" style={{ color: '#21ACCD' }}></i>
                        Analyses détaillées
                    </h5>
                    <Row className="g-3">
                        <Col sm={6} xl={3}>
                            <div className="quick-link-card" onClick={() => navigate('/stats/ventes/ca')}>
                                <div className="quick-link-icon">
                                    <i className="ri-money-euro-circle-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Chiffre d'affaires</h6>
                                <small className="text-muted">Évolution et tendances</small>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="quick-link-card" onClick={() => navigate('/stats/ventes/top-produits')}>
                                <div className="quick-link-icon">
                                    <i className="ri-star-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Top produits</h6>
                                <small className="text-muted">Les plus vendus</small>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="quick-link-card" onClick={() => navigate('/stats/achats/depenses')}>
                                <div className="quick-link-icon">
                                    <i className="ri-shopping-cart-2-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Dépenses achats</h6>
                                <small className="text-muted">Analyse des coûts</small>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="quick-link-card" onClick={() => navigate('/stats/stock/rotation')}>
                                <div className="quick-link-icon">
                                    <i className="ri-refresh-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Rotation stock</h6>
                                <small className="text-muted">Performance</small>
                            </div>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                /* Value Cards */
                .value-card {
                    border-radius: 20px;
                    padding: 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    transition: all 0.3s ease;
                    height: 100%;
                }
                .value-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
                }
                .value-card-primary {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                }
                .value-card-success {
                    background: linear-gradient(135deg, #10b981, #059669);
                }
                .value-card-warning {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                }
                .value-card-content {
                    flex: 1;
                }
                .value-card-badge {
                    display: inline-block;
                    background: rgba(255,255,255,0.25);
                    padding: 0.3rem 1rem;
                    border-radius: 20px;
                    font-size: 0.7rem;
                    font-weight: 500;
                    color: white;
                    margin-bottom: 1rem;
                }
                .value-card-amount {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: white;
                    margin: 0 0 0.5rem 0;
                }
                .value-card-footer {
                    font-size: 0.7rem;
                    color: rgba(255,255,255,0.7);
                    margin: 0;
                }
                .value-card-icon {
                    width: 50px;
                    height: 50px;
                    background: rgba(255,255,255,0.25);
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .value-card-icon i {
                    font-size: 1.5rem;
                    color: white;
                }
                
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-info { border-left-color: #0ea5e9; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 8px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Stock Value Circle */
                .stock-value-circle {
                    width: 120px;
                    height: 120px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                
                /* Alert Card */
                .alert-card {
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                }
                .alert-card:hover {
                    transform: translateX(5px);
                    border-color: #21ACCD;
                }
                .alert-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Quick Link Card */
                .quick-link-card {
                    background: white;
                    border-radius: 16px;
                    padding: 1.5rem;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: 1px solid #e2e8f0;
                    height: 100%;
                }
                .quick-link-card:hover {
                    transform: translateY(-5px);
                    border-color: #21ACCD;
                    box-shadow: 0 20px 25px -5px rgba(33, 172, 205, 0.2);
                }
                .quick-link-icon {
                    width: 64px;
                    height: 64px;
                    background: #e2f3f5;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1rem;
                    transition: all 0.3s ease;
                }
                .quick-link-icon i {
                    font-size: 1.8rem;
                    color: #21ACCD;
                }
                .quick-link-card:hover .quick-link-icon {
                    transform: scale(1.1) rotate(5deg);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}